/**
 * What the line simulation knows about each tool that the route does not.
 *
 * The route says how long an operation takes (wafers per hour) and which tool
 * runs it. It says nothing about CAPACITY: how many chambers a tool has, how
 * many lots a furnace boat holds, how often it breaks and for how long, and
 * which chambers reject their heat into the building's process cooling
 * water. Those are the facts that decide how a fault in the sub-fab turns
 * into a queue on the floor, and they live here, beside the capital table in
 * flow/model.ts that prices the same tools.
 *
 * ONE PLATFORM, SEVERAL CHAMBERS. The floor has one etcher, ETCH-01, and it
 * is modelled as what an ICP etcher actually is: a cluster tool with three
 * process chambers on one wafer-handling platform. Two of them cool through
 * the house PCW loop; the third has a local chiller under the platform. That
 * is the whole mechanism of the chiller scenario - a trip in the sub-fab
 * takes two chambers down and leaves one running - and it is ordinary
 * equipment practice rather than a contrivance for the lesson.
 *
 * FAILURES ARE WHAT MAKE A FAB QUEUE. A tool that never breaks, fed lots at
 * 85% of its capacity, barely queues at all: the first cut of this line ran
 * at 1.03 times raw process time, which no fab has ever done. What puts the
 * hours into a real cycle time is that a chamber goes down for four hours
 * every few days, and four hours is seven lots' worth of work at that
 * chamber, all of whom now wait. MTBF and MTTR here are typical of the tool
 * class - availability in the low to mid nineties - and they are what the
 * operating curve's bend is made of.
 *
 * Chamber counts elsewhere are set so that at 85% load on the bottleneck the
 * rest of the floor sits between 15% and 80%, which is what a line looks like
 * when one tool family is the constraint. None of them are measured; they are
 * the smallest counts that keep the other tools off the critical path, and
 * the harness prints every utilisation so a change here is a change you see.
 */

export interface LineToolSpec {
  /** Asset tag, as the route names it. */
  tag: string
  /** Process chambers (or heads, cells, probers) that work lots in parallel. */
  chambers: number
  /**
   * How many of this tool the BUILDING has, where that is not one per bay.
   *
   * Every other family scales with the bays: ten bays, ten etchers. Three
   * tools do not, and pretending they did put ten EUV scanners on the line at
   * three per cent each. A fab buys one or two of the most expensive tool
   * there is and spends it only where the pitch demands it; a CD-SEM serves
   * several bays' worth of exposures; and the fridges are a farm in one room.
   * The count is the building's, whatever `scale` is.
   */
  units?: number
  /**
   * Lots per run for a batch tool. A furnace boat takes several lots at once
   * and the run takes as long whether the boat is full or not; every other
   * tool works one lot per chamber.
   */
  batch?: number
  /**
   * Longest a batch tool waits for the boat to fill before running short
   * [h]. A furnace that waited for four lots of the same recipe at this
   * line's pace would wait a day; the rule real furnaces run is "full, or
   * the oldest lot has waited long enough".
   */
  batchMaxWaitH?: number
  /**
   * Which chambers reject heat to the house process-cooling-water loop, by
   * index. These go down when the PCW runs warm; the others keep running.
   */
  pcwCooled?: number[]
  /** Mean hours between failures of one chamber. Omit for a tool that does not fail. */
  mtbfH?: number
  /** Mean hours to repair it. */
  mttrH?: number
}

export const LINE_TOOLS: LineToolSpec[] = [
  { tag: 'ETCH-01', chambers: 3, pcwCooled: [0, 1], mtbfH: 60, mttrH: 4 },
  { tag: 'DEP-02', chambers: 4, mtbfH: 50, mttrH: 3 },
  { tag: 'CLN-09', chambers: 2, mtbfH: 120, mttrH: 2 },
  { tag: 'PRB-14', chambers: 2, mtbfH: 80, mttrH: 2 },
  { tag: 'ECD-12', chambers: 2, mtbfH: 60, mttrH: 3 },
  { tag: 'CMP-11', chambers: 2, mtbfH: 40, mttrH: 3 },
  { tag: 'THRM-04', chambers: 1, batch: 4, batchMaxWaitH: 1, mtbfH: 200, mttrH: 8 },
  { tag: 'MET-05', chambers: 1, mtbfH: 100, mttrH: 1 },
  { tag: 'CZ-07', chambers: 1, mtbfH: 200, mttrH: 8 },
  { tag: 'DICE-15', chambers: 1, mtbfH: 80, mttrH: 2 },
  { tag: 'LITH-03', chambers: 1, mtbfH: 100, mttrH: 6 },
  // The three stations a product mix brings onto the line (scenario.ts,
  // NEW_TOOLS_MIX). None is on the single-DUV reference route, so none of
  // them touches a default run.
  //
  // ONE scanner for the building, at about a third loaded by a quarter of
  // the starts - and down one hour in eight, which is what EUV availability
  // is and why a fab with one of them plans around it. Its drive laser and
  // source reject their heat into the house loop: a chiller trip takes it
  // down with the etch chambers.
  { tag: 'EUV-22', chambers: 1, units: 1, pcwCooled: [0], mtbfH: 60, mttrH: 8 },
  // Two CD-SEMs: every EUV layer is measured before it is etched.
  { tag: 'SEM-23', chambers: 1, units: 2, mtbfH: 120, mttrH: 2 },
  // A farm of four fridges. A pulse-tube compressor is water-cooled, so the
  // same trip stops every one of them. A fridge that fails on its own has to
  // be warmed, opened and cooled again, which is why its repair is a day.
  { tag: 'QDT-24', chambers: 1, units: 4, pcwCooled: [0], mtbfH: 300, mttrH: 24 },
  { tag: 'WPR-08', chambers: 1, mtbfH: 100, mttrH: 3 },
  { tag: 'FT-18', chambers: 1, mtbfH: 100, mttrH: 2 },
  { tag: 'IMP-10', chambers: 1, mtbfH: 40, mttrH: 4 },
  { tag: 'EPI-20', chambers: 1, mtbfH: 80, mttrH: 6 },
  { tag: 'BOND-16', chambers: 1, mtbfH: 60, mttrH: 2 },
  { tag: 'PKG-17', chambers: 1, mtbfH: 100, mttrH: 2 },
  { tag: 'INSP-13', chambers: 1, mtbfH: 150, mttrH: 1 },
]

export const lineToolSpec = (tag: string): LineToolSpec =>
  LINE_TOOLS.find((t) => t.tag === tag) ?? { tag, chambers: 1 }
